import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Clock, FileText, Shield, ArrowRight, Phone, Mail, MapPin } from "lucide-react";

const packages = [
  {
    name: "Document Organization",
    price: "$99",
    description: "We prepare, you submit and follow up",
    features: [
      "Review of available documentation",
      "Timeline organization assistance",
      "Evidence checklist preparation",
      "Structured case summary document",
      "You submit through official channels",
    ],
    turnaround: "2-3 business days",
    popular: false,
  },
  {
    name: "Full Service",
    price: "$399",
    description: "We prepare, we submit, we follow up",
    features: [
      "Everything in Document Organization",
      "Comprehensive timeline structuring",
      "Evidence compilation and formatting",
      "Structured case summary document",
      "We handle submission and follow-up",
    ],
    turnaround: "5-7 business days",
    popular: true,
  },
  {
    name: "Priority",
    price: "$499",
    description: "Expedited preparation with priority support",
    features: [
      "Everything in Full Service",
      "Priority processing queue",
      "Expedited 24-hour preparation",
      "Direct communication channel",
    ],
    turnaround: "24 hours",
    popular: false,
  },
];

export default function CaseReviewPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    platform: "",
    situation: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Redirect to contact page with intake form
    window.location.href = "/contact";
  };

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-500/90 flex items-center justify-center font-bold text-slate-950">
              SD
            </div>
            <div className="font-semibold tracking-wide">Social Dispute Co.</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-blue-100/80">
            <Link to="/platform-appeal-guide" className="hover:text-white transition-colors">How It Works</Link>
            <a href="#packages" className="hover:text-white transition-colors">Packages</a>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <Link 
          to="/platform-appeal-guide" 
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm mb-6"
        >
          ← Back to Guide
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold">Documentation Preparation Services</h1>
        <p className="mt-4 text-lg text-slate-300 max-w-2xl">
          We help organize timeline and evidence into structured document sets for 
          submission through official platform channels.
        </p>
      </section>

      {/* Scope & Process */}
      <section className="bg-slate-900/50 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">Scope of Services</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-blue-400 mb-4">What's Included</h3>
              <ul className="space-y-3 text-slate-300">
                <li className="flex gap-3">
                  <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Organization of your account timeline and history</span>
                </li>
                <li className="flex gap-3">
                  <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Compilation of evidence and supporting materials</span>
                </li>
                <li className="flex gap-3">
                  <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Structured case summary preparation</span>
                </li>
                <li className="flex gap-3">
                  <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Information about official appeal channels</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-400 mb-4">What's Not Included</h3>
              <ul className="space-y-3 text-slate-400">
                <li>• Account access or login services</li>
                <li>• Direct contact with platforms on your behalf</li>
                <li>• Legal advice or representation</li>
                <li>• Guarantees of any outcome</li>
              </ul>
              <div className="mt-6 p-4 rounded-lg border border-slate-700 bg-slate-800/50">
                <p className="text-sm text-slate-400">
                  <strong className="text-slate-300">Note:</strong> You are responsible for submitting 
                  the prepared documentation through official platform channels. Platform decisions 
                  are entirely at the platform's discretion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-8">Documentation Preparation Services</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {packages.map((pkg, index) => (
            <div 
              key={pkg.name}
              className={`rounded-2xl border p-6 ${
                pkg.popular 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-white/10 bg-white/5'
              }`}
            >
              {pkg.popular && (
                <span className="text-xs font-bold text-blue-400 uppercase tracking-wide">
                  Most Selected
                </span>
              )}
              <h3 className="text-xl font-semibold mt-2">{pkg.name}</h3>
              <p className="mt-2 text-3xl font-bold text-blue-300">{pkg.price}</p>
              <p className="mt-2 text-sm text-slate-400">{pkg.description}</p>
              
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-400">
                <Clock className="w-4 h-4" />
                <span>Turnaround: {pkg.turnaround}</span>
              </div>

              <ul className="mt-6 space-y-3">
                {pkg.features.map((feature) => (
                  <li key={`${pkg.name}-${feature}`} className="flex gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/pricing"
                className={`mt-6 block text-center rounded-xl px-5 py-3 font-semibold transition-colors ${
                  pkg.popular 
                    ? 'bg-blue-500 text-slate-950 hover:bg-blue-400' 
                    : 'border border-white/20 text-white hover:bg-white/5'
                }`}
                data-testid={`select-package-${index}`}
              >
                Select Package
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          No outcomes are guaranteed. Platform decisions are outside our control.
        </p>
      </section>

      {/* Refund Policy Summary */}
      <section className="bg-slate-900/50 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <Shield className="w-8 h-8 text-blue-400 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold mb-2">Refund Policy</h2>
              <p className="text-slate-300 mb-4">
                If the platform issues a final denial after our documentation process is complete, 
                a partial refund of $200 may be available. See our full refund policy for eligibility 
                and terms.
              </p>
              <Link 
                to="/refund-policy" 
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                Read Full Refund Policy →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-8">Request a Case Review</h2>
        <div className="grid md:grid-cols-2 gap-10">
          {/* Contact Info */}
          <div>
            <p className="text-slate-300 mb-6">
              To request a case review, contact us using the information below or complete 
              our intake form. We'll review your situation and explain your options.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm text-slate-400">Email</p>
                  <a href="mailto:intake@socialdisputeco.com" className="text-white hover:text-blue-400">
                    intake@socialdisputeco.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm text-slate-400">Phone</p>
                  <a href="tel:808-301-2134" className="text-white hover:text-blue-400">
                    808-301-2134
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm text-slate-400">Location</p>
                  <p className="text-white">Honolulu, Hawaii, United States</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-6 py-3 font-semibold text-slate-950 hover:bg-blue-400 transition-colors"
                data-testid="start-intake-btn"
              >
                Start Intake Form
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Quick Info Box */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <h3 className="text-lg font-semibold mb-4">Before You Contact Us</h3>
            <p className="text-slate-400 text-sm mb-4">
              Please have the following information ready:
            </p>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>• Platform name (Instagram, Facebook, TikTok, etc.)</li>
              <li>• Type of account issue (disabled, suspended, restricted)</li>
              <li>• Approximate date of the issue</li>
              <li>• Any previous appeal attempts</li>
              <li>• Available evidence or documentation</li>
            </ul>
            <div className="mt-6 p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10">
              <p className="text-xs text-yellow-100">
                <strong>Note:</strong> Submitting an inquiry does not guarantee service availability. 
                We review each case individually and may decline cases outside our scope.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-white mb-4">Business Information</h3>
              <p className="text-slate-400">Social Dispute Co</p>
              <p className="text-slate-400">Independent Documentation Preparation Service</p>
              <p className="text-slate-400 mt-3">Email: intake@socialdisputeco.com</p>
              <p className="text-slate-400">Phone: 808-301-2134</p>
              <p className="text-slate-400">Honolulu, Hawaii, United States</p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <div className="flex flex-col gap-2">
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors">About Us</Link>
                <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">Contact</Link>
                <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms-of-service" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link>
                <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
                <Link to="/disclaimer" className="text-slate-400 hover:text-white transition-colors">Disclaimer</Link>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">About</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Social Dispute Co is an independent documentation preparation service. 
                We are not affiliated with any social media platform. We do not guarantee outcomes.
              </p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-sm text-slate-500">
            <p>© 2025 Social Dispute Co. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}
